import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  FolderArrowDownIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { FolderTreeEntry } from '@xphoto/shared';
import { rovingTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { useI18n } from '../i18n';
import type { FolderOpKind } from '../organize/FolderOpModal';
import { thumbnailUrl } from './api';
import { interpolate } from './format';
import { folderLinkFor } from './url-state';
import { DENSITIES, type Density } from './config';
import { filterFolders, sortFolders, type FolderSortDir, type FolderSortKey } from './folder-sort';
import { getFolderSort, storeFolderSort, type FolderSortPref } from './folder-sort-pref';
import { ToolsMenu, type ToolAction } from './ToolsMenu';

/**
 * Grille de CARTES de dossier (Lot H — refonte de l'onglet Dossiers).
 *
 * Remplace l'ancienne LISTE à gauche : chaque dossier de 1er niveau devient une
 * carte = vignette de couverture (la « première image du dossier », `coverAssetId`)
 * + nom du dossier + compteur, cliquable pour DESCENDRE dedans (`/dossiers?dossier=<path>`).
 *
 * - La densité S/M/L dimensionne les cartes (hauteur de vignette) ET choisit la
 *   taille de vignette servie (256 en S/M, 1024 en L), comme la grille d'images.
 * - Un petit contrôle DÉDIÉ trie les cartes par nom ou date + sens (le `SortMenu`
 *   photo, avec ses critères Vues/Note/Type/Aléatoire, ne s'applique PAS aux dossiers).
 * - Un champ de filtre EN LIGNE (dans l'en-tête, à côté du tri) filtre les cartes par
 *   nom (client, en direct). Il remplace l'ancienne boîte de recherche de la barre
 *   d'outils (fusionnée dans le panneau « Recherche ») pour cet onglet.
 * - Placeholder (icône) si le dossier n'a pas de couverture ou si l'image échoue.
 *
 * XPHOTO-32 : la grille affiche des `FolderTreeEntry` (index des dossiers ⋈ facettes,
 * `ADR-0013` amendé) et non plus de simples facettes SQL — elle montre donc AUSSI les
 * répertoires VIDES, invisibles pour les facettes. Deux états sont signalés sur la carte
 * PAR DU TEXTE ET UNE ICÔNE, jamais par la couleur seule (WCAG AA) :
 *  - dossier VIDE (`count === 0`) : pastille « Vide » + icône de dossier ouvert ;
 *  - dossier NON INDEXÉ (`indexed === false`) : pastille « Absent de l'index » + icône
 *    d'alerte — des photos y sont indexées mais le dernier scan n'a pas retenu ce
 *    répertoire (répertoire exclu du listage, ou index des dossiers en retard).
 *
 * XPHOTO-32 (lot 4) : chaque carte porte un menu « ⋯ » (Renommer / Déplacer / Nouveau
 * sous-dossier) quand l'utilisateur a l'accès écriture (`useCanWrite`, résolu par
 * l'appelant et passé en `onFolderAction`). Deux points d'attention :
 *
 *  - LE MENU N'EST PAS DANS LE LIEN. La carte était un `<Link>` englobant tout ; y
 *    imbriquer un bouton donnerait du HTML invalide (contenu interactif dans un `<a>`)
 *    et un piège d'accessibilité — le lecteur d'écran annonce un lien dont l'intérieur
 *    est cliquable autrement. La carte est donc devenue un conteneur `relative` dont le
 *    lien et le menu sont deux ENFANTS FRÈRES. Aucun `stopPropagation` n'est nécessaire :
 *    le clic sur le menu ne traverse jamais le lien, il n'y est pas.
 *  - LE MENU EST VISIBLE AU TACTILE : `opacity-0 group-hover:…
 *    [@media(hover:none)]:opacity-100` — une media-query, pas une détection JavaScript.
 *    Sur un écran sans survol, il n'y a pas de survol à attendre. La case de sélection des
 *    VIGNETTES suivait la même règle jusqu'à la v1.40.1 ; elle ne la suit plus (une case
 *    permanente sur chaque photo rendait la grille illisible sur téléphone) et dépend
 *    désormais du MODE sélection (cf. `Thumbnail.tsx`). Ici, la règle tient toujours :
 *    il y a QUELQUES cartes par écran, pas des centaines de vignettes, et le « ⋯ » d'une
 *    carte est le seul accès aux gestes de dossier au tactile — il n'a pas de mode.
 */

/** Hauteur de vignette d'une carte selon la densité (S plus petite que la grille). */
const CARD_HEIGHT: Record<Density, number> = {
  S: 120,
  M: 180,
  L: 260,
};

/** Largeur minimale d'une carte selon la densité (les cartes se répartissent en grille). */
const CARD_MIN_WIDTH: Record<Density, number> = {
  S: 140,
  M: 200,
  L: 280,
};

interface FolderGridProps {
  folders: FolderTreeEntry[];
  loading: boolean;
  errorCode: string | null;
  density: Density;
  /** Recherche libre (filtre les cartes par nom, client) — pilotée par l'URL galerie. */
  search: string;
  /** Met à jour la recherche libre depuis le champ de filtre EN LIGNE (en direct). */
  onSearchChange: (value: string) => void;
  /** Ref du conteneur défilant (restauration de la position au retour, Lot H). */
  scrollRef?: React.Ref<HTMLDivElement> | undefined;
  /**
   * Appelé JUSTE avant de descendre dans un dossier (clic sur une carte) → l'appelant
   * réserve la position de défilement pour la restaurer au retour (Lot H).
   */
  onDrillIn?: (() => void) | undefined;
  /**
   * Mode EMBARQUÉ (vue Dossiers combinée, façon explorateur) : la grille rend les
   * cartes à HAUTEUR NATURELLE et NE possède PAS son propre conteneur défilant — c'est
   * le parent qui borne la hauteur et gère le défilement. Défaut `false` (vue autonome
   * pleine hauteur avec scroll interne).
   */
  embedded?: boolean | undefined;
  /**
   * Ouvre une écriture de dossier (XPHOTO-32 lot 4). ABSENT ⇒ AUCUN menu « ⋯ » n'est
   * rendu : c'est ainsi que l'accès lecture seule est traité — les affordances
   * d'écriture sont MASQUÉES, pas seulement désactivées (discipline `useCanWrite`).
   * L'appelant résout le droit ; la grille ne fait que rendre ce qu'on lui donne.
   */
  onFolderAction?: ((kind: FolderOpKind, path: string) => void) | undefined;
}

export function FolderGrid({
  folders,
  loading,
  errorCode,
  density,
  search,
  onSearchChange,
  scrollRef,
  onDrillIn,
  embedded = false,
  onFolderAction,
}: FolderGridProps): React.JSX.Element {
  const { t } = useI18n();
  // Critère + sens PERSISTÉS (`folder-sort-pref.ts`) : la grille est remontée à chaque
  // descente/remontée dans l'arborescence, un état purement local repartait donc à
  // « Nom croissant » à chaque niveau. Hors URL à dessein (ce n'est pas un emplacement).
  const [folderSort, setFolderSortState] = useState<FolderSortPref>(getFolderSort);
  const setFolderSort = useCallback((next: FolderSortPref) => {
    setFolderSortState(next);
    storeFolderSort(next);
  }, []);

  const thumbSize = DENSITIES[density].thumbnailSize;

  // Filtre (par nom, client) PUIS tri (par nom/date + sens, client). La liste des
  // dossiers de 1er niveau est petite → tout se fait côté client, sans endpoint.
  const visible = useMemo(
    () => sortFolders(filterFolders(folders, search), folderSort.key, folderSort.dir),
    [folders, search, folderSort],
  );

  const gridStyle = {
    gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH[density]}px, 1fr))`,
  } as const;

  return (
    <section
      aria-label={t('folders.title')}
      className={embedded ? 'flex flex-col gap-3' : 'flex min-h-0 flex-1 flex-col gap-3'}
    >
      {/* Contrôle de tri DÉDIÉ aux dossiers (critère + sens) — segmenté, icône + texte. */}
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl<FolderSortKey>
          label={t('folders.sortBy')}
          value={folderSort.key}
          onChange={(key) => setFolderSort({ ...folderSort, key })}
          options={[
            { value: 'name', label: t('folders.sortName') },
            { value: 'date', label: t('folders.sortDate') },
          ]}
        />
        <SegmentedControl<FolderSortDir>
          label={t('folders.sortOrder')}
          value={folderSort.dir}
          onChange={(dir) => setFolderSort({ ...folderSort, dir })}
          options={[
            { value: 'asc', label: t('folders.sortAsc') },
            { value: 'desc', label: t('folders.sortDesc') },
          ]}
        />

        {/* Filtre EN LIGNE des cartes de dossier par nom (client, en direct). */}
        <div className="relative">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute start-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label={t('folders.filterLabel')}
            placeholder={t('folders.filterPlaceholder')}
            className="h-9 w-40 rounded-md border border-border-strong bg-surface ps-9 pe-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:w-56"
          />
        </div>
      </div>

      {loading && (
        <div className="grid gap-3" style={gridStyle} aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-md bg-surface-2 motion-reduce:animate-none"
              style={{ height: CARD_HEIGHT[density] + 44 }}
            />
          ))}
        </div>
      )}

      {!loading && errorCode && (
        <p role="alert" className="text-sm text-danger">
          {t('gallery.errorTitle')}
        </p>
      )}

      {!loading && !errorCode && visible.length === 0 && (
        <p className="py-8 text-sm text-muted">{t('folders.empty')}</p>
      )}

      {!loading &&
        !errorCode &&
        visible.length > 0 &&
        // Vue autonome : la grille possède son propre conteneur défilant (scroll interne
        // borné). Vue EMBARQUÉE : cartes à hauteur naturelle, le parent gère le défilement.
        (embedded ? (
          <ul className="grid gap-3" style={gridStyle}>
            {visible.map((folder) => (
              <li key={folder.path}>
                <FolderCard
                  folder={folder}
                  density={density}
                  thumbSize={thumbSize}
                  onDrillIn={onDrillIn}
                  onFolderAction={onFolderAction}
                />
              </li>
            ))}
          </ul>
        ) : (
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
            <ul className="grid gap-3" style={gridStyle}>
              {visible.map((folder) => (
                <li key={folder.path}>
                  <FolderCard
                    folder={folder}
                    density={density}
                    thumbSize={thumbSize}
                    onDrillIn={onDrillIn}
                    onFolderAction={onFolderAction}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
    </section>
  );
}

/**
 * Petit contrôle segmenté accessible (radiogroup). Option active = `aria-checked` +
 * fond/teinte (jamais la couleur seule : le libellé texte est le repère principal).
 *
 * Clavier : flèches + `tabIndex` mobile (`rovingFocus.ts`), SÉLECTION QUI SUIT LE FOCUS
 * (motif ARIA standard). Le tri des dossiers est purement CLIENT (`sortFolders` sur les
 * données déjà chargées) : une flèche ne coûte rien.
 */
function SegmentedControl<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
}): React.JSX.Element {
  const onKeyDown = useRovingRadioGroup('auto');
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted">{label}</span>
      <div
        role="radiogroup"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="inline-flex rounded-md border border-border p-0.5"
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={rovingTabIndex(active)}
              onClick={() => onChange(opt.value)}
              className={`rounded px-2.5 py-1 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
                active ? 'bg-brand/10 font-semibold text-brand' : 'text-fg hover:bg-surface-2'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FolderCard({
  folder,
  density,
  thumbSize,
  onDrillIn,
  onFolderAction,
}: {
  folder: FolderTreeEntry;
  density: Density;
  thumbSize: 256 | 1024;
  onDrillIn?: (() => void) | undefined;
  onFolderAction?: ((kind: FolderOpKind, path: string) => void) | undefined;
}): React.JSX.Element {
  const { t, tp } = useI18n();
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const name = folder.path === '' ? t('folders.root') : folder.path;
  const isEmpty = folder.count === 0;
  const isGhost = !folder.indexed;

  // ÉTAT DE LA CARTE, rendu par du TEXTE (pastille lisible + libellé accessible) et une
  // icône DISTINCTE — jamais par la couleur seule (WCAG AA). Sans cette pastille, un
  // dossier vide ne se distinguerait que par son compteur « 0 », ce qui est trop discret
  // pour l'information la plus neuve de cet écran.
  const stateLabel = isGhost
    ? t('folders.notIndexed')
    : isEmpty && folder.hasSubfolders
      ? t('folders.subfoldersOnly')
      : isEmpty
        ? t('folders.emptyBadge')
        : null;

  const openLabel = interpolate(t('folders.open'), { name });
  // Le lecteur d'écran entend l'état AVEC le nom, sans avoir à atteindre la pastille.
  const cardLabel = stateLabel === null ? openLabel : `${openLabel} — ${stateLabel}`;
  const countLabel = tp('folders.countLabel', folder.count);
  const cover = folder.coverAssetId;
  const showPlaceholder = cover === null || failed;

  return (
    // Conteneur `relative` : le lien de navigation et le menu d'écriture sont deux
    // enfants FRÈRES (cf. l'en-tête du fichier). `group` porte la révélation au survol.
    <div className="group relative">
      {onFolderAction !== undefined && (
        <div className="absolute end-1 top-1 z-10 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none [@media(hover:none)]:opacity-100">
          <FolderActionsMenu path={folder.path} onFolderAction={onFolderAction} />
        </div>
      )}
      <Link
        to={folderLinkFor(folder.path)}
        aria-label={cardLabel}
        title={cardLabel}
        onClick={onDrillIn}
        className="flex flex-col rounded-md border border-border bg-surface transition-colors hover:border-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
      >
        {/* Vignette de couverture (ou placeholder). */}
        <div
          className="relative w-full overflow-hidden rounded-t-md bg-surface-2"
          style={{ height: CARD_HEIGHT[density] }}
        >
          {showPlaceholder ? (
            <div className="flex h-full w-full items-center justify-center text-muted">
              {/* Icône DISTINCTE par état (redondante avec la pastille texte, jamais
                seule porteuse de l'information) : alerte pour un dossier absent du
                disque, dossier OUVERT pour un dossier vide, dossier fermé sinon. */}
              {isGhost ? (
                <ExclamationTriangleIcon className="h-10 w-10" aria-hidden="true" />
              ) : cover === null ? (
                isEmpty ? (
                  <FolderOpenIcon className="h-10 w-10" aria-hidden="true" />
                ) : (
                  <FolderIcon className="h-10 w-10" aria-hidden="true" />
                )
              ) : (
                <PhotoIcon className="h-10 w-10" aria-hidden="true" />
              )}
            </div>
          ) : (
            <>
              {!loaded && (
                <div className="absolute inset-0 animate-pulse bg-surface-2 motion-reduce:animate-none" />
              )}
              <img
                src={thumbnailUrl(cover, thumbSize)}
                alt=""
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                onLoad={() => setLoaded(true)}
                onError={() => setFailed(true)}
                className={`h-full w-full object-cover transition-opacity duration-200 motion-reduce:transition-none ${
                  loaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </>
          )}
        </div>

        {/* Nom du dossier + état + compteur EN DESSOUS de la vignette. */}
        <div className="flex items-center gap-2 px-2.5 py-2">
          <FolderIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">{name}</span>
          {/* Pastille d'état TEXTUELLE (« Vide », « Absent de l'index »…). `aria-hidden` :
            l'état est déjà dans l'`aria-label` du lien, l'annoncer deux fois serait du
            bruit pour un lecteur d'écran. */}
          {stateLabel !== null && (
            <span
              aria-hidden="true"
              className="shrink-0 rounded border border-border px-1.5 py-0.5 text-xs text-muted"
            >
              {stateLabel}
            </span>
          )}
          <span className="shrink-0 text-xs text-muted">{countLabel}</span>
        </div>
      </Link>
    </div>
  );
}

/**
 * Menu « ⋯ » d'une carte de dossier : Renommer / Déplacer vers… / Nouveau sous-dossier,
 * puis Supprimer (XPHOTO-32 lot 5).
 *
 * Réutilise `ToolsMenu` — sa mécanique d'accessibilité (rôle `menu`, flèches, `Échap`,
 * clic extérieur, entrées désactivées enjambées) est UNE dans le projet et n'a pas à
 * être redessinée ici.
 *
 * « Supprimer » occupe la place que le lot 4 lui avait RÉSERVÉE : en dernier, derrière un
 * séparateur (`separatorBefore`) et en variante `danger`. Les trois entrées connues n'ont
 * donc pas bougé sous le doigt de l'utilisateur. La variante `danger` teinte le libellé,
 * mais le sens est porté par l'ICÔNE (corbeille) et le MOT — jamais par la couleur seule.
 */
function FolderActionsMenu({
  path,
  onFolderAction,
}: {
  path: string;
  onFolderAction: (kind: FolderOpKind, path: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const actions: ToolAction[] = [
    {
      id: 'rename',
      label: t('folderOps.actionRename'),
      icon: <PencilSquareIcon className="h-5 w-5 shrink-0" aria-hidden="true" />,
      onSelect: () => onFolderAction('rename', path),
    },
    {
      id: 'move',
      label: t('folderOps.actionMove'),
      icon: <FolderArrowDownIcon className="h-5 w-5 shrink-0" aria-hidden="true" />,
      onSelect: () => onFolderAction('move', path),
    },
    {
      id: 'create',
      label: t('folderOps.actionCreateChild'),
      icon: <FolderPlusIcon className="h-5 w-5 shrink-0" aria-hidden="true" />,
      onSelect: () => onFolderAction('create', path),
    },
    {
      id: 'delete',
      label: t('folderOps.actionDelete'),
      icon: <TrashIcon className="h-5 w-5 shrink-0" aria-hidden="true" />,
      onSelect: () => onFolderAction('delete', path),
      separatorBefore: true,
      danger: true,
    },
  ];
  return (
    <ToolsMenu
      trigger={<EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />}
      label={interpolate(t('folderOps.menuLabel'), { name: path })}
      closeLabel={t('a11y.closeMenu')}
      actions={actions}
    />
  );
}
